import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ExternalLink, ArrowLeft } from 'lucide-react';
import { TimeFilter, PeriodType } from '../stats/TimeFilter';
import {
  DataTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EmptyState
} from '../common/ui';
import Pagination from '../common/Pagination';
import { formatDateTime } from '../../utils/dateUtils';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

type OperationFilter = 'all' | 'repair' | 'exchange';
type RepairTypeFilter = 'all' | 'paid' | 'free_of_charge' | 'warranty' | 'unknown';

interface ModuleOperation {
  id: number;
  timestamp: string;
  event_type: 'repair_module' | 'exchange_module';
  module_name: string | null;
  description: string | null;
  repair_type: 'paid' | 'free_of_charge' | 'warranty' | 'unknown';
  module_attributes: Record<string, string> | null;
  old_serial_number: string | null;
  new_serial_number: string | null;
  repair_id: number | null;
  customer_name: string | null;
  device_id: number;
  device_serial: string | null;
  product_name: string | null;
  performed_by_name: string | null;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const OPERATION_FILTERS: { key: OperationFilter; label: string }[] = [
  { key: 'all', label: 'All operations' },
  { key: 'repair', label: 'Repaired' },
  { key: 'exchange', label: 'Exchanged' }
];

const REPAIR_TYPE_FILTERS: { key: RepairTypeFilter; label: string }[] = [
  { key: 'all', label: 'All types' },
  { key: 'paid', label: 'Paid' },
  { key: 'free_of_charge', label: 'Free of charge' },
  { key: 'warranty', label: 'Warranty' }
];

const REPAIR_TYPE_LABELS: Record<string, string> = {
  paid: 'Paid',
  free_of_charge: 'Free of charge',
  warranty: 'Warranty',
  unknown: 'Unknown'
};

const REPAIR_TYPE_BADGES: Record<string, string> = {
  paid: 'bg-gray-100 text-gray-700',
  free_of_charge: 'bg-green-100 text-green-700',
  warranty: 'bg-amber-100 text-amber-700',
  unknown: 'bg-gray-100 text-gray-400'
};

const VALID_PERIODS = ['all', '7d', '30d', '365d', 'this_month', 'this_year', 'custom'];

const ModuleReport: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && VALID_PERIODS.includes(p)) return p as PeriodType;
    if (searchParams.get('start') && searchParams.get('end')) return 'custom';
    const saved = getUserPreference('moduleReport', 'period', 'all');
    return VALID_PERIODS.includes(saved) && saved !== 'custom' ? (saved as PeriodType) : 'all';
  });
  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');

  const [operation, setOperation] = useState<OperationFilter>(() => {
    const o = searchParams.get('operation');
    return o === 'repair' || o === 'exchange' ? o : 'all';
  });
  const [repairType, setRepairType] = useState<RepairTypeFilter>(() => {
    const t = searchParams.get('repair_type');
    return t && REPAIR_TYPE_FILTERS.some(f => f.key === t) ? (t as RepairTypeFilter) : 'all';
  });
  const [selectedModules, setSelectedModules] = useState<string[]>(() => searchParams.getAll('modules'));

  const [page, setPage] = useState(() => Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1));
  const [limit, setLimit] = useState(() => {
    const saved = parseInt(getUserPreference('moduleReport', 'limit', '50'), 10);
    return [10, 20, 50, 100].includes(saved) ? saved : 50;
  });

  const [moduleOptions, setModuleOptions] = useState<string[]>([]);
  const [operations, setOperations] = useState<ModuleOperation[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/repairs/module-report/modules')
      .then(res => setModuleOptions(res.data.modules || []))
      .catch(err => console.error('Error fetching module list:', err));
  }, []);

  const syncUrl = useCallback((next: {
    period: PeriodType; start: string; end: string;
    operation: OperationFilter; repairType: RepairTypeFilter;
    modules: string[]; page: number;
  }) => {
    const params = new URLSearchParams();
    if (next.period === 'custom' && next.start && next.end) {
      params.set('start', next.start);
      params.set('end', next.end);
    } else if (next.period !== 'all') {
      params.set('period', next.period);
    }
    if (next.operation !== 'all') params.set('operation', next.operation);
    if (next.repairType !== 'all') params.set('repair_type', next.repairType);
    next.modules.forEach(m => params.append('modules', m));
    if (next.page > 1) params.set('page', String(next.page));
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (period === 'custom' && customStart && customEnd) {
        params.set('start', customStart);
        params.set('end', customEnd);
      } else {
        params.set('period', period);
      }
      if (operation !== 'all') params.set('operation', operation);
      if (repairType !== 'all') params.set('repair_type', repairType);
      selectedModules.forEach(m => params.append('modules', m));
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await axios.get(`/api/repairs/module-report?${params.toString()}`);
      setOperations(response.data.operations || []);
      setPagination(response.data.pagination || null);
    } catch (err: any) {
      console.error('Error fetching module report:', err);
      setError(err.response?.data?.error || 'Failed to fetch module report');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd, operation, repairType, selectedModules, page, limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const applyFilters = (changes: Partial<{
    period: PeriodType; start: string; end: string;
    operation: OperationFilter; repairType: RepairTypeFilter; modules: string[];
  }>) => {
    const next = {
      period: changes.period ?? period,
      start: changes.start ?? customStart,
      end: changes.end ?? customEnd,
      operation: changes.operation ?? operation,
      repairType: changes.repairType ?? repairType,
      modules: changes.modules ?? selectedModules,
      page: 1
    };
    setPeriod(next.period);
    setCustomStart(next.start);
    setCustomEnd(next.end);
    setOperation(next.operation);
    setRepairType(next.repairType);
    setSelectedModules(next.modules);
    setPage(1);
    syncUrl(next);
  };

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    if (newPeriod !== 'custom') {
      setUserPreference('moduleReport', 'period', newPeriod);
    }
    if (newPeriod === 'custom' && start && end) {
      applyFilters({ period: newPeriod, start, end });
    } else {
      applyFilters({ period: newPeriod, start: '', end: '' });
    }
  };

  const toggleModule = (name: string) => {
    const next = selectedModules.includes(name)
      ? selectedModules.filter(m => m !== name)
      : [...selectedModules, name];
    applyFilters({ modules: next });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    syncUrl({ period, start: customStart, end: customEnd, operation, repairType, modules: selectedModules, page: newPage });
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setUserPreference('moduleReport', 'limit', newLimit);
    setLimit(newLimit);
    setPage(1);
  };

  // Group modules by product prefix ("Oudie N - LCD" → "Oudie N")
  const moduleGroups = moduleOptions.reduce<Record<string, string[]>>((groups, name) => {
    const group = name.includes(' - ') ? name.split(' - ')[0] : 'Other';
    (groups[group] = groups[group] || []).push(name);
    return groups;
  }, {});
  const GROUP_ORDER: Record<string, number> = { 'Oudie N': 1, 'Omni': 2, 'Legacy Oudie': 3, 'Other': 99 };
  const orderedGroups = Object.entries(moduleGroups).sort(([a], [b]) =>
    (GROUP_ORDER[a] ?? 50) - (GROUP_ORDER[b] ?? 50) || a.localeCompare(b)
  );

  const chipClass = (active: boolean) =>
    `px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
      active
        ? 'bg-naviter-blue text-white border-naviter-blue'
        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800'
    }`;

  return (
    <div className="px-4 pt-0 pb-20">
      <div className="mb-2">
        <button
          onClick={() => navigate('/repairs')}
          className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 mb-1"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back to Repairs
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Module Report</h1>
      </div>

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
        includeAllTime
        compact
      />

      <div className="bg-white rounded-lg shadow p-3 mb-3 space-y-3">
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium text-gray-700">Operation:</p>
            {OPERATION_FILTERS.map(f => (
              <button key={f.key} onClick={() => applyFilters({ operation: f.key })} className={chipClass(operation === f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:border-l sm:border-gray-300 sm:pl-6">
            <p className="text-xs font-medium text-gray-700">Repair type:</p>
            {REPAIR_TYPE_FILTERS.map(f => (
              <button key={f.key} onClick={() => applyFilters({ repairType: f.key })} className={chipClass(repairType === f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1.5">
            <p className="text-xs font-medium text-gray-700">Modules:</p>
            <button
              onClick={() => applyFilters({ modules: [] })}
              className={chipClass(selectedModules.length === 0)}
            >
              All modules
            </button>
          </div>
          <div className="rounded-md overflow-hidden border border-gray-200">
            {orderedGroups.map(([group, names]) => (
              <div key={group} className="flex items-start gap-3 px-2.5 py-1.5 odd:bg-gray-100 even:bg-white">
                <span className="text-xs font-semibold text-naviter-blue uppercase tracking-wider w-24 flex-shrink-0 pt-1.5">
                  {group}
                </span>
                <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                  {names.map(name => (
                    <button
                      key={name}
                      onClick={() => toggleModule(name)}
                      className={chipClass(selectedModules.includes(name))}
                    >
                      {name.includes(' - ') ? name.split(' - ').slice(1).join(' - ') : name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6 animate-pulse space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-4 bg-gray-200 rounded" />
          ))}
        </div>
      ) : (
        // Escape Layout's max-w-7xl only as much as the table's content needs:
        // the outer div offers the full viewport, the inner one grows from the
        // normal page width (76rem) up to that only when columns demand it.
        <div className="sm:mx-[calc(50%-50vw+1.5rem)] flex justify-center">
          <div className="w-fit min-w-[min(100%,76rem)] max-w-full">
          {pagination && (
            <div className="mb-2 text-xs text-gray-600">
              <span className="font-semibold text-gray-900">{pagination.totalItems}</span>
              {pagination.totalItems === 1 ? ' result' : ' results'}
            </div>
          )}
          <DataTable
            emptyState={operations.length === 0 ? (
              <EmptyState message="No module operations match the selected filters" />
            ) : undefined}
          >
            <TableHeader>
              <tr>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Module</TableHeaderCell>
                <TableHeaderCell>Operation</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell>Details</TableHeaderCell>
                <TableHeaderCell>Customer</TableHeaderCell>
                <TableHeaderCell>Device</TableHeaderCell>
                <TableHeaderCell responsive="lg">By</TableHeaderCell>
                <TableHeaderCell>Repair</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {operations.map(op => (
                <TableRow key={op.id}>
                  <TableCell className="align-top">{formatDateTime(op.timestamp)}</TableCell>
                  <TableCell className="align-top font-medium whitespace-pre-wrap max-w-[9rem]">{op.module_name || '—'}</TableCell>
                  <TableCell className="align-top">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      op.event_type === 'exchange_module'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {op.event_type === 'exchange_module' ? 'Exchanged' : 'Repaired'}
                    </span>
                  </TableCell>
                  <TableCell className="align-top">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${REPAIR_TYPE_BADGES[op.repair_type] || REPAIR_TYPE_BADGES.unknown}`}>
                      {REPAIR_TYPE_LABELS[op.repair_type] || op.repair_type}
                    </span>
                  </TableCell>
                  <TableCell className="align-top whitespace-pre-wrap max-w-[15rem] min-w-[12rem]">
                    {op.description ? (
                      <div className="line-clamp-3" title={op.description}>{op.description}</div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top whitespace-pre-wrap max-w-xs">
                    {op.module_attributes && Object.keys(op.module_attributes).length > 0 && (
                      <div className="space-y-0.5">
                        {Object.entries(op.module_attributes).map(([attr, value]) => (
                          <div key={attr}>
                            <span className="text-gray-500">{attr.includes(' - ') ? attr.split(' - ').slice(1).join(' - ') : attr}:</span>{' '}
                            <span className="font-medium">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(op.old_serial_number || op.new_serial_number) && (
                      <div className="mt-0.5">
                        <span className="text-gray-500">S/N:</span>{' '}
                        <span className="font-medium">{op.old_serial_number || '?'} → {op.new_serial_number || '?'}</span>
                      </div>
                    )}
                    {!op.module_attributes && !op.old_serial_number && !op.new_serial_number && (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top whitespace-pre-wrap max-w-[10rem]">{op.customer_name || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="align-top">
                    <Link to={`/devices/id/${op.device_id}/view`} className="text-naviter-blue hover:underline">
                      {op.device_serial || `#${op.device_id}`}
                    </Link>
                    {op.product_name && (
                      <div className="text-gray-500">{op.product_name}</div>
                    )}
                  </TableCell>
                  <TableCell responsive="lg" className="align-top whitespace-pre-wrap max-w-[8rem]">{op.performed_by_name || '—'}</TableCell>
                  <TableCell className="align-top">
                    {op.repair_id ? (
                      <Link
                        to={`/repairs/${op.repair_id}/view`}
                        className="inline-flex items-center gap-1 text-naviter-blue hover:underline font-medium"
                      >
                        #{op.repair_id}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>

          {pagination && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleReport;
